import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';

function CheckPLAccountSorted() {
  const navigate = useNavigate();

  // State for API data
  const [blockData, setBlockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiCallMadeRef = useRef(false);
  const isLoadingRef = useRef(false);

  // State for date and time
  const [dateTime, setDateTime] = useState({ dateStr: '', timeStr: '' });

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const getAuthConfig = () => {
    if (!hasAuthSession()) return null;
    return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
  };

  // Fetch Block data
  const fetchBlockData = useCallback(async () => {
    // Prevent multiple API calls using refs
    if (apiCallMadeRef.current || isLoadingRef.current) {
      console.log('API call already in progress or completed');
      return;
    }

    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      setLoading(false);
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0] // Extract year from format like "2022-23"
    };

    // Check if we already called API for this exact DSL and Year
    const callKey = `${blockInfo.dslNo}-${blockInfo.surveyYear}`;
    if (apiCallMadeRef.current === callKey) {
      console.log('API already called for this DSL and Year combination');
      return;
    }

    apiCallMadeRef.current = callKey;
    isLoadingRef.current = true;
    showLoading(true);
    setLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        setLoading(false);
        isLoadingRef.current = false;
        apiCallMadeRef.current = false;
        return;
      }

      console.log('Fetching Block data with payload:', requestPayload);
      const response = await axios.post(`${API_URL}api/block/get-data`, requestPayload, config);
      console.log('Block API Response:', response.data);
      
      const { data, status } = response.data;

      if (status && data && data.length > 0) {
        setBlockData(data);
        console.log('Block data loaded successfully:', data.length, 'items');
      } else {
        setBlockData([]);
        toast.warning('No Block data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockData([]);
    } finally {
      showLoading(false);
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Load data on component mount - only once
  useEffect(() => {
    // Only call if we have required data and haven't called yet
    if (blockInfo.dslNo && blockInfo.surveyYear && !apiCallMadeRef.current) {
      fetchBlockData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Get current date and time - only once on component mount
  useEffect(() => {
    const now = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = days[now.getDay()];
    const monthName = months[now.getMonth()];
    const date = now.getDate();
    const year = now.getFullYear();
    
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const period = now.getHours() >= 12 ? 'PM' : 'AM';
    
    setDateTime({
      dateStr: `${dayName}, ${monthName} ${date}, ${year}`,
      timeStr: `${hours}:${minutes}:${seconds} ${period}`
    });
  }, []); // Empty dependency array - runs only once on mount

  // Group data by block and item code
  const groupedData = () => {
    const groups = {};
    let grandTotal = 0;

    blockData.forEach((item) => {
      // Parse blockItem to extract Block and Item Code
      const parseBlockItem = (blockItem) => {
        if (!blockItem) return { block: '', itemCode: '', column: '' };

        // Handle formats like "Block-F, Item-3" or "Block-F,Item-3"
        const match = blockItem.match(/Block-([A-Z]),\s*Item-(\d+)/i);
        if (match) {
          return {
            block: match[1],
            itemCode: match[2],
            column: ''
          };
        }

        // Fallback: try to extract just the block letter
        const blockMatch = blockItem.match(/Block-([A-Z])/i);
        if (blockMatch) {
          return {
            block: blockMatch[1],
            itemCode: '',
            column: ''
          };
        }

        return { block: '', itemCode: '', column: '' };
      };

      const { block, itemCode } = parseBlockItem(item.blockItem);
      const groupKey = `${block}_${itemCode}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          block,
          itemCode,
          items: [],
          subtotal: 0
        };
      }

      groups[groupKey].items.push(item);
      
      if (item.value !== undefined && item.value !== null) {
        groups[groupKey].subtotal += Number(item.value);
        grandTotal += Number(item.value);
      }
    });

    return { groups, grandTotal };
  };

  const { groups, grandTotal } = groupedData();

  const { dateStr, timeStr } = dateTime;

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <style>
        {`
          .pl-account-header {
            background-color: #e3f2fd;
            padding: 15px 20px;
            border-bottom: 2px solid #1976d2;
            margin-bottom: 20px;
          }
          .page-title {
            color: #1976d2;
            font-size: 24px;
            font-weight: bold;
            margin: 0;
          }
          .dispatch-number {
            color: #333;
            font-size: 16px;
            font-weight: 500;
            margin: 0;
          }
          .table th {
            background-color: #ff9800;
            color: #ffffff;
            font-weight: bold;
            font-size: 12px;
            text-align: center;
            vertical-align: middle;
            padding: 12px 8px;
            border: 1px solid #f57c00;
            white-space: normal;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            line-height: 1.4;
            word-wrap: break-word;
            hyphens: auto;
          }
          .table td {
            border: 1px solid #dee2e6;
            padding: 6px 4px;
            font-size: 10px;
            text-align: center;
            vertical-align: middle;
            font-weight: 500;
          }
          .table tbody tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          .table tbody tr:nth-child(odd) {
            background-color: #ffffff;
          }
          .table tbody tr:hover {
            background-color: #e3f2fd !important;
            transition: background-color 0.2s ease;
          }
          .item-name {
            text-align: left;
            font-weight: 600;
            color: #2c3e50;
            max-width: 200px;
            word-wrap: break-word;
            line-height: 1.3;
          }
          .block-cell {
            text-align: center !important;
            font-weight: 600;
            color: #1976d2;
            font-size: 12px;
            vertical-align: middle;
          }
          .item-code {
            text-align: center !important;
            font-weight: 600;
            color: #666;
            font-size: 12px;
            vertical-align: middle;
          }
          .numeric-cell {
            text-align: right;
            font-family: 'Courier New', monospace;
            font-weight: 500;
            color: #2c3e50;
          }
          .block-header-row {
            background-color: #f0f0f0 !important;
          }
          .block-header {
            background-color: #f0f0f0;
            font-weight: 600;
            color: #1976d2;
            font-size: 12px;
            padding: 10px 6px;
            text-align: left;
            border: 1px solid #dee2e6;
          }
          .subtotal-row {
            background-color: #fff9e6 !important;
            font-weight: 600;
          }
          .subtotal-label {
            text-align: right;
            font-weight: 600;
            color: #666;
            padding: 8px 6px;
          }
          .subtotal-value {
            background-color: #fff9e6;
            font-weight: 700;
            color: #ff9800;
            border: 1px solid #ffe0b2;
          }
          .grand-total-row {
            background-color: #f3e5f5 !important;
            font-weight: 700;
          }
          .grand-total-label {
            text-align: right;
            font-weight: 700;
            color: #7b1fa2;
            padding: 12px 6px;
            font-size: 12px;
          }
          .grand-total-value {
            background-color: #f3e5f5;
            font-weight: 800;
            color: #7b1fa2;
            border: 2px solid #ce93d8;
            padding: 10px 6px;
            font-size: 12px;
          }
          .text-right {
            text-align: right;
          }
          .footer-section {
            background-color: #f8f9fa;
            padding: 15px 20px;
            border-top: 1px solid #dee2e6;
            margin-top: 20px;
          }
          .footer-page {
            color: #666;
            font-size: 14px;
            text-align: center;
            margin: 0;
          }
          .table-responsive {
            overflow-x: auto;
            max-width: 100%;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          @media (min-width: 1200px) {
            .table-responsive {
              overflow-x: visible;
            }
          }
          .table {
            margin-bottom: 0;
            border-radius: 8px;
            overflow: hidden;
          }
          .table thead th:first-child {
            border-top-left-radius: 8px;
          }
          .table thead th:last-child {
            border-top-right-radius: 8px;
          }
          .green-icon {
            color: #4caf50;
            font-size: 20px;
            margin-right: 10px;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
              font-size: 10px;
              line-height: 1.2;
            }
            .container-fluid {
              padding: 0 !important;
              margin: 0 !important;
            }
            .card {
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
            }
            .card-body {
              padding: 5px !important;
            }
            .btn {
              display: none !important;
            }
            .table {
              border-collapse: collapse !important;
              width: 100% !important;
              font-size: 7px !important;
              table-layout: fixed !important;
            }
            .table th, .table td {
              border: 1px solid #000 !important;
              padding: 3px 2px !important;
              font-size: 7px !important;
              white-space: normal !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }
            .table th {
              white-space: normal !important;
              word-wrap: break-word !important;
              line-height: 1.1 !important;
              height: auto !important;
            }
            .table th:nth-child(1), .table td:nth-child(1) { width: 35% !important; }
            .table th:nth-child(2), .table td:nth-child(2) { width: 12.5% !important; }
            .table th:nth-child(3), .table td:nth-child(3) { width: 12.5% !important; }
            .table th:nth-child(4), .table td:nth-child(4) { width: 20% !important; }
            .table th:nth-child(5), .table td:nth-child(5) { width: 20% !important; }
            .table th {
              background-color: #f8f9fa !important;
              font-weight: bold !important;
              text-align: center !important;
            }
            .table td {
              text-align: center !important;
            }
            .item-name {
              text-align: left !important;
            }
            .block-cell {
              text-align: center !important;
            }
            .item-code {
              text-align: center !important;
            }
            .numeric-cell {
              text-align: right !important;
              font-family: 'Courier New', monospace !important;
            }
            @page {
              margin: 0.5in;
              size: A4 landscape;
            }
            .footer-section {
              margin-top: 10px !important;
            }
          }
        `}
      </style>
      <div className="container-fluid py-3">
        {/* Header Section */}
        <div className="pl-account-header">
          <div className="row align-items-center">
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <i className="fa fa-book green-icon"></i>
                <h1 className="page-title">Check PL Account</h1>
              </div>
            </div>
            <div className="col-md-4 text-center">
              <p className="dispatch-number" style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                <span>DSL No: {blockInfo.dslNo || 'Not Available'}</span>
                <span>Survey Year: {blockInfo.surveyYear || 'Not Available'}</span>
              </p>
            </div>
            <div className="col-md-4 text-end">
              <p className="dispatch-number date-time">{dateStr}</p>
              <p className="dispatch-number date-time">{timeStr}</p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="card mx-3 mb-3">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '70%', minWidth: '300px' }}>ITEM</th>
                    <th style={{ width: '30%', minWidth: '100px' }}>CLOSING</th>
                  </tr>
                </thead>
                <tbody>
                  {blockData.length > 0 ? (
                    <>
                      {Object.entries(groups).map(([groupKey, group]) => (
                        <React.Fragment key={groupKey}>
                          <tr className="block-header-row">
                            <td colSpan="2" className="block-header">
                              block: {group.block} /Item code: {group.itemCode} /column:
                            </td>
                          </tr>
                          {group.items.map((item, index) => (
                            <tr key={`${groupKey}-${index}`}>
                              <td className="item-name">{item.itemName || ''}</td>
                              <td className="numeric-cell">{item.value !== undefined && item.value !== null ? Number(item.value).toLocaleString() : ''}</td>
                            </tr>
                          ))}
                          <tr className="subtotal-row">
                            <td className="text-right subtotal-label">Subtotal:</td>
                            <td className="numeric-cell subtotal-value">{Number(group.subtotal).toLocaleString()}</td>
                          </tr>
                        </React.Fragment>
                      ))}
                      <tr className="grand-total-row">
                        <td className="text-right grand-total-label">Grand Total:</td>
                        <td className="numeric-cell grand-total-value">{Number(grandTotal).toLocaleString()}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="2" className="text-center text-muted">
                        {loading ? 'Loading data...' : 'No data available for this DSL No. and Survey Year'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="footer-section">
          <div className="row align-items-center">
            <div className="col-12">
              <p className="footer-page">Page 1 of 1</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">

            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/validation')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/check-pl-account')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/net-profit-tally')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default CheckPLAccountSorted; 